import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { enginePhysicalData } from '../conversion/engine';
import { geometryPhysicalData, quaySlope } from '../conversion/geometry';
import { physicalData, relativeDensity, shieldsParameter, stabilityParameter, thickness } from '../conversion/physicalData';
import {
    TResultPhysicalData,
    isOkMinimumRevetmentThickness,
    isOkMinimumRevetmentWidthBProtection,
    isOkUnderKeelClearance,
    minimumRevetmentThickness,
    minimumRevetmentWidthBProtection,
    resultPhysicalData,
    underKeelClearance,
} from '../conversion/result';
import { scenarioPhysicalData } from '../conversion/scenario';
import { getPropellerDirectionWithRespectToTheQuayLabel, getTypeOfPropellerLabel, getTypeOfQuayLabel } from '../design/CommonResult';
import { getNameScenario } from '../design/MultiScenario';
import { ENumberOfPropellers, EPropellerDirectionWithRespectToTheQuay, ETypeOfQuay, EUnitOfMeasure } from '../shared/enum';
import { TInputEngine, TInputGeometry, TInputScenario } from '../shared/iProject';
import { TResult } from '../shared/iResult';
import variable from '../shared/style/variable';
import { redux } from '../store';
import LogoMaccaferri from './LogoMaccaferri.png';
import Flag from './flag.png';
import Warning from './warning.png';
import RobotoBold from './Roboto-Bold.ttf';
import RobotoRegular from './Roboto-Regular.ttf';
import { getImageFromScenario } from '../design/scenario/engine/util';

Font.register({
    family: 'Roboto',
    fonts: [
        { src: RobotoBold, fontWeight: 700 },
        { src: RobotoRegular, fontWeight: 400 },
    ],
});

const styles = StyleSheet.create({
    page: {
        fontSize: 8,
        flexDirection: 'column',
        backgroundColor: variable.white,
    },
    section: {
        padding: '10 30',
    },
    tableContainer: { flexDirection: 'column' },
    tableHeader: { padding: '0 5', height: 30, backgroundColor: variable.green, color: variable.white, flexDirection: 'column', justifyContent: 'center' },
    tableRow: { justifyContent: 'space-between', flexDirection: 'row', padding: '0 5' },
    tableCell: { height: 20, flexDirection: 'column', justifyContent: 'center', padding: '0 5' },
    h1: {
        fontSize: 12,
        fontWeight: 600,
        color: variable.green,
    },
    h2: {
        fontSize: 8,
        fontWeight: 600,
        color: variable.green,
    },
    h3: {
        fontSize: 8,
        color: variable.greenLight,
    },
    bold: {
        fontFamily: 'Roboto',
        fontWeight: 700,
    },
    regular: {
        fontFamily: 'Roboto',
        fontWeight: 400,
    },
});

export const Pdf = () => {
    const project = redux.getState().project;
    const result = redux.getState().result;
    const engine = redux.getState().engine;
    const map = redux.getState().map;

    const { t } = useTranslation();

    const inputGeometry = {
        typeOfQuay: getTypeOfQuayLabel(project.data!.geometry!.typeOfQuay),
        quaySlope: project.data?.geometry?.typeOfQuay == ETypeOfQuay.Vertical ? undefined : project.data!.geometry!.quaySlope,
        pileDiameter: project.data?.geometry?.typeOfQuay == ETypeOfQuay.SlopedWithPiles ? project.data!.geometry!.pileDiameter : undefined,
        distanceBetweenPileAxis: project.data?.geometry?.typeOfQuay == ETypeOfQuay.SlopedWithPiles ? project.data!.geometry!.distanceBetweenPileAxis : undefined,
        minimumWaterDepth: project.data!.geometry!.minimumWaterDepth,
        designWaterLevel: project.data!.geometry!.designWaterLevel,
        designDredgingLevel: project.data!.geometry!.designDredgingLevel,
        minimumUnderKeelClerance: project.data!.geometry!.minimumUnderKeelClerance,
        distanceBetweenVesselAndQuay: project.data!.geometry!.distanceBetweenVesselAndQuay,
    };

    const inputMaterial = {
        thickness: project.data?.doubleLayerMaterial
            ? project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                ? `${project.data?.material?.physicalData[0].thickness} + ${project.data?.doubleLayerMaterial?.physicalData[0].thickness}`
                : `${project.data?.material?.physicalData[0].thicknessIn} + ${project.data?.doubleLayerMaterial?.physicalData[0].thicknessIn}`
            : project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
            ? project.data?.material?.physicalData[0].thickness
            : project.data?.material?.physicalData[0].thicknessIn,
        rockD50: project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? project.data?.material?.physicalData[0].rockD50 : project.data?.material?.physicalData[0].rockD50in,
        equivalentThickness:
            project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric ? project.data?.material?.physicalData[0].equivalentThickness : project.data?.material?.physicalData[0].equivalentThicknessIn,
        shieldsParameter: project.data?.material?.physicalData[0].shieldsParameter,
        relativeDensity: project.data?.material?.physicalData[0].relativeDensity,
        geotextileFilter: project.data?.material?.geotextileFilter,
        xties: project.data?.material?.xties,
        doubleLayerAtTransition: project.data?.doubleLayerMaterial ? !!project.data?.doubleLayerMaterial : null,
        material: project.data?.doubleLayerMaterial ? project.data!.doubleLayerMaterial.translations![0].name : null,
        doubleMaterialThickness: project.data?.doubleLayerMaterial
            ? project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                ? project.data?.doubleLayerMaterial?.physicalData[0].thickness
                : project.data?.doubleLayerMaterial?.physicalData[0].thicknessIn
            : null,
    };

    const header = (
        <View style={styles.section} fixed>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <View style={{ flexDirection: 'row' }}>
                    <Image src={LogoMaccaferri} style={{ height: 30, paddingRight: 20 }} />
                    <View style={{ flexDirection: 'column' }}>
                        <View style={{ flexDirection: 'row', paddingBottom: 2 }}>
                            <Text style={styles.bold}>MacQuay Design  </Text>
                            <Text> – </Text>
                            <Text>Technical Report</Text>
                        </View>
                        <Text>Design of protection systems against scour caused by ships</Text>
                    </View>
                </View>
                <View style={{ flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Text>edesign.maccaferri.com</Text>
                    <Text render={({ pageNumber, totalPages }) => t('pageOf', { pageNumber, totalPages })} fixed />
                </View>
            </View>
            <View style={{ marginTop: 20, border: `1px solid ${variable.green}` }}></View>
        </View>
    );

    const footer = <View style={{ margin: '0 30', border: `1px solid ${variable.green}` }} fixed></View>;

    return (
        <Document>
            <Page size='A4' style={styles.page}>
                {header}
                <View style={{ height: '85%' }}>
                    <View>
                        <View style={styles.section}>
                            <View style={{ flexDirection: 'row' }}>
                                <View style={{ width: '75%', flexDirection: 'column' }}>
                                    <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('projectInfo')}</Text>
                                    <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('name')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.title}</Text>
                                        </View>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('companyName')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.companyName}</Text>
                                        </View>
                                    </View>
                                    <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('client')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.client}</Text>
                                        </View>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('projectNumber')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.projectNumber}</Text>
                                        </View>
                                    </View>
                                    <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('country')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.country}</Text>
                                        </View>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('city')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.city}</Text>
                                        </View>
                                    </View>
                                    <View style={{ flexDirection: 'row', padding: '5 0' }}>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('latitude')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.latitude}</Text>
                                        </View>
                                        <View style={{ width: '50%', flexDirection: 'column' }}>
                                            <Text style={styles.h2}>{t('longitude')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.longitude}</Text>
                                        </View>
                                    </View>
                                    {project.data?.project?.notes && (
                                        <View style={{ padding: '5 0' }}>
                                            <Text style={styles.h2}>{t('notes')}</Text>
                                            <Text style={styles.h3}>{project.data?.project?.notes}</Text>
                                        </View>
                                    )}
                                </View>
                                <View style={{ width: '35%', backgroundColor: variable.greyLight }}>
                                    <Image src={map.data!.image} />
                                </View>
                            </View>
                        </View>
                        <View style={styles.section}>
                            <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('summaryInput')}</Text>
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                <View style={{ ...styles.tableContainer, width: '48%' }}>
                                    <View style={styles.tableHeader}>
                                        <Text>{t('geometry')}</Text>
                                    </View>
                                    {Object.entries(inputGeometry)
                                        .filter(x => x[1] !== undefined)
                                        .map((x, u) => {
                                            let key = t(x[0], {
                                                unitOfMeasure: geometryPhysicalData[x[0] as TInputGeometry].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                            });
                                            let arr = key.split(' ');
                                            let el = <Text>{key}</Text>;
                                            if (x[0] == quaySlope) {
                                                el = (
                                                    <Text style={styles.regular}>
                                                        {' ' + arr[0] + ' ' + arr[1] + ' ' + '('}&#945;{')' + ' ' + arr[3]}
                                                    </Text>
                                                );
                                            }
                                            return (
                                                <View
                                                    style={{
                                                        ...styles.tableRow,
                                                        backgroundColor: u % 2 === 0 ? variable.greyLight : variable.white,
                                                        color: u % 2 === 0 ? variable.greenLight : variable.black,
                                                    }}>
                                                    <View style={styles.tableCell}>{el}</View>
                                                    <View style={styles.tableCell}>
                                                        <Text>{x[1]}</Text>
                                                    </View>
                                                </View>
                                            );
                                        })}
                                </View>
                                <View style={{ ...styles.tableContainer, flexDirection: 'column', width: '48%' }}>
                                    <View style={{ ...styles.tableHeader }}>
                                        <View style={{ flexDirection: 'row' }}>
                                            <Text>{t(`material`) + '-'}</Text>
                                            <Text style={styles.bold}>{project.data?.material?.translations![0].name}</Text>
                                        </View>
                                    </View>
                                    {Object.entries(inputMaterial)
                                        .filter(x => x[1])
                                        .map((x, u) => {
                                            let key: string = t(
                                                x[0],
                                                physicalData[x[0] as IPhysicalData] && {
                                                    unitOfMeasure: physicalData[x[0] as IPhysicalData].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                                },
                                            );
                                            let arr = key.split(' ');
                                            let el = <Text>{key}</Text>;
                                            if (x[0] == thickness) {
                                                el = <Text style={styles.bold}>{key}</Text>;
                                            } else if (x[0] == relativeDensity) {
                                                el = (
                                                    <Text style={styles.regular}>
                                                        {' ' + arr[0] + ' ' + arr[1] + ' ' + '('}&#916;{')'}
                                                    </Text>
                                                );
                                            } else if (x[0] == shieldsParameter) {
                                                el = (
                                                    <Text style={styles.regular}>
                                                        {' ' + arr[0] + ' ' + arr[1] + ' ' + '('}&#936;{'cr)'}
                                                    </Text>
                                                );
                                            }
                                            return (
                                                <View
                                                    style={{
                                                        ...styles.tableRow,
                                                        backgroundColor: u % 2 === 0 ? variable.greyLight : variable.white,
                                                        color: u % 2 === 0 ? variable.greenLight : variable.black,
                                                    }}>
                                                    <View style={{ ...styles.tableCell }}>{el}</View>
                                                    <View style={styles.tableCell}>
                                                        <Text>{typeof x[1] == 'boolean' ? (x[1] ? t('yes') : t('no')) : x[1]}</Text>
                                                    </View>
                                                </View>
                                            );
                                        })}
                                </View>
                            </View>
                        </View>
                        <View style={styles.section}>
                            <Text style={{ ...styles.h1, paddingBottom: 10 }}>{t('validation')}</Text>
                            <View style={styles.tableContainer}>
                                <View style={{ flexDirection: 'row', borderBottom: `0.3px solid ${variable.greyLight}` }}>
                                    <View style={{ ...styles.tableHeader, width: '25%' }}>
                                        <Text>{t('scenarios')}</Text>
                                    </View>
                                    <View style={{ ...styles.tableHeader, width: '25%', borderLeft: `0.3px solid ${variable.greyLight}` }}>
                                        <Text>
                                            {t('minimumRevetmentThickness', { unitOfMeasure: resultPhysicalData['minimumRevetmentThickness']?.unitOfMeasure[project.data!.project!.unitOfMeasure!] })}
                                        </Text>
                                    </View>
                                    <View style={{ ...styles.tableHeader, width: '25%', borderLeft: `0.3px solid ${variable.greyLight}` }}>
                                        <Text>
                                            {t('minimumRevetmentWidthBProtection', {
                                                unitOfMeasure: resultPhysicalData['minimumRevetmentWidthBProtection']?.unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                            })}
                                        </Text>
                                    </View>
                                    <View style={{ ...styles.tableHeader, width: '25%', borderLeft: `0.3px solid ${variable.greyLight}` }}>
                                        <Text>
                                            {t('minimumUnderKeelClerance', { unitOfMeasure: geometryPhysicalData['minimumUnderKeelClerance']?.unitOfMeasure[project.data!.project!.unitOfMeasure!] })}
                                        </Text>
                                    </View>
                                </View>
                                <View style={{ flexDirection: 'row' }}>
                                    <View style={{ ...styles.tableHeader, width: '25%' }}>
                                        <Text></Text>
                                    </View>
                                    {[0, 1, 2].map(x => (
                                        <View style={{ width: '25%', flexDirection: 'row', backgroundColor: variable.green }}>
                                            <View style={{ ...styles.tableHeader, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                <Text>{t('value')}</Text>
                                            </View>
                                            <View style={{ ...styles.tableHeader, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                <Text>{t('calculated')}</Text>
                                            </View>
                                            <View style={{ ...styles.tableHeader, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                <Text>{t('check')}</Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                                {project.data?.scenarios?.map((x, i) => {
                                    let obj = [
                                        result.data!.openPiledQuayScenarios?.[i],
                                        result.data!.propellerAxisParallelToTheQuayStructureScenarios?.[i],
                                        result.data!.propellerAxisTransverseToSlopedQuayStructureScenarios?.[i],
                                        result.data!.propellerAxisTransverseToVerticalQuayStructureScenarios?.[i],
                                    ]
                                        .filter(x => x !== undefined)
                                        .find(y => Object.values(y).some(x => x !== null && x !== false));

                                    let isOkMinimumRevetmentWidthBProtection = true;
                                    if (
                                        result.data!.propellerAxisParallelToTheQuayStructureScenarios &&
                                        Object.values(result.data!.propellerAxisParallelToTheQuayStructureScenarios[i]!).some(x => x !== null && x !== false)
                                    ) {
                                        isOkMinimumRevetmentWidthBProtection = result.data!.propellerAxisParallelToTheQuayStructureScenarios[i].isOkMinimumRevetmentWidthBProtection!;
                                    }

                                    let isOk = obj!.isOkMinimumRevetmentThickness! && isOkMinimumRevetmentWidthBProtection && result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance!;

                                    let thickness = project.data?.doubleLayerMaterial
                                        ? project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                                            ? `${project.data.doubleLayerMaterial.physicalData[0].thickness} + ${project.data?.material?.physicalData[0].thickness!}`
                                            : `${project.data.doubleLayerMaterial.physicalData[0].thicknessIn} + ${project.data?.material?.physicalData[0].thicknessIn!}`
                                        : project.data?.project?.unitOfMeasure == EUnitOfMeasure.Metric
                                        ? project.data?.material?.physicalData[0].thickness
                                        : project.data?.material?.physicalData[0].thicknessIn;

                                    return (
                                        <View
                                            key={x.id}
                                            style={{
                                                border: isOk ? '' : `1px solid ${variable.red}`,
                                                backgroundColor: i % 2 === 0 ? variable.greyLight : variable.white,
                                                color: i % 2 === 0 ? variable.greenLight : variable.black,
                                                flexDirection: 'row',
                                            }}>
                                            <View style={{ ...styles.tableCell, width: '25%' }}>
                                                <Text style={{ color: isOk ? (i % 2 === 0 ? variable.greenLight : variable.black) : variable.red }}>
                                                    {getNameScenario(x, project.data!.engines![i])}
                                                </Text>
                                            </View>
                                            <View style={{ width: '25%', flexDirection: 'row' }}>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    <Text style={{ color: obj?.isOkMinimumRevetmentThickness ? (i % 2 === 0 ? variable.greenLight : variable.black) : variable.red }}>{thickness}</Text>
                                                </View>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    <Text style={{ color: obj?.isOkMinimumRevetmentThickness ? (i % 2 === 0 ? variable.greenLight : variable.black) : variable.red }}>
                                                        {obj!.minimumRevetmentThickness}
                                                    </Text>
                                                </View>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    {obj?.isOkMinimumRevetmentThickness ? (
                                                        <Image src={Flag} style={{ height: 15, width: 15, margin: 'auto' }} />
                                                    ) : (
                                                        <Image src={Warning} style={{ height: 15, width: 15, margin: 'auto' }} />
                                                    )}
                                                </View>
                                            </View>
                                            <View style={{ width: '25%', flexDirection: 'row' }}>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    <Text
                                                        style={{
                                                            color: isOkMinimumRevetmentWidthBProtection ? (i % 2 === 0 ? variable.greenLight : variable.black) : variable.red,
                                                        }}>
                                                        {project.data!.material?.physicalData[0].widthOfTheProtectionW ?? '-'}
                                                    </Text>
                                                </View>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    <Text
                                                        style={{
                                                            color: isOkMinimumRevetmentWidthBProtection ? (i % 2 === 0 ? variable.greenLight : variable.black) : variable.red,
                                                        }}>
                                                        {result.data!.propellerAxisParallelToTheQuayStructureScenarios?.[i].minimumRevetmentWidthBProtection ?? '-'}
                                                    </Text>
                                                </View>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    {result.data!.propellerAxisParallelToTheQuayStructureScenarios?.[i].minimumRevetmentWidthBProtection ? (
                                                        isOkMinimumRevetmentWidthBProtection ? (
                                                            <Image src={Flag} style={{ height: 15, width: 15, margin: 'auto' }} />
                                                        ) : (
                                                            <Image src={Warning} style={{ height: 15, width: 15, margin: 'auto' }} />
                                                        )
                                                    ) : (
                                                        '-'
                                                    )}
                                                </View>
                                            </View>
                                            <View style={{ width: '25%', flexDirection: 'row' }}>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    <Text
                                                        style={{
                                                            color: result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance
                                                                ? i % 2 === 0
                                                                    ? variable.greenLight
                                                                    : variable.black
                                                                : variable.red,
                                                        }}>
                                                        {project.data!.geometry?.minimumUnderKeelClerance}
                                                    </Text>
                                                </View>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    <Text
                                                        style={{
                                                            color: result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance
                                                                ? i % 2 === 0
                                                                    ? variable.greenLight
                                                                    : variable.black
                                                                : variable.red,
                                                        }}>
                                                        {result.data!.flowVelocityAtThePropellers![i].underKeelClearance}
                                                    </Text>
                                                </View>
                                                <View style={{ ...styles.tableCell, width: '33%', borderLeft: `0.3px solid ${variable.greyLight}`, textAlign: 'center' }}>
                                                    {result.data!.flowVelocityAtThePropellers![i].isOkUnderKeelClearance ? (
                                                        <Image src={Flag} style={{ height: 15, width: 15, margin: 'auto' }} />
                                                    ) : (
                                                        <Image src={Warning} style={{ height: 15, width: 15, margin: 'auto' }} />
                                                    )}
                                                </View>
                                            </View>
                                        </View>
                                    );
                                })}
                            </View>
                        </View>
                    </View>
                </View>
                {footer}
            </Page>
            {project.data?.scenarios?.map((x, i) => {
                const inputScenario = {
                    vesselType: x.vesselType,
                    typeOfPropeller: getTypeOfPropellerLabel(project.data!.engines![i].typeOfPropeller),
                    propellerDirectionWithRespectToTheQuay: getPropellerDirectionWithRespectToTheQuayLabel(project.data!.engines![i].propellerDirectionWithRespectToTheQuay),
                    numberOfPropellers: project.data!.engines![i].numberOfPropellers,
                    draft: x.draft,
                    vesselBeamBs: x.vesselBeamBs,
                    distanceBetweenPropellerAxes: project.data!.engines![i].numberOfPropellers == ENumberOfPropellers.Single ? undefined : project.data!.engines![i].distanceBetweenPropellerAxes,
                    propellerDiameter: project.data!.engines![i].propellerDiameter,
                    propellerEquippedWithNozzles: x.propellerEquippedWithNozzles ? t('yes') : t('no'),
                    maximumPowerOfThePropeller: project.data!.engines![i].maximumPowerOfThePropeller,
                    percentageOfUseInPortManeuvers: project.data!.engines![i].percentageOfUseInPortManeuvers,
                    effectivePowerUsedByThePropeller: x.effectivePowerUsedByThePropeller,
                    coefficentC3: x.coefficentC3,
                    verticalDistanceBetweenTheAxisOfThePropellerAndTheKell: x.verticalDistanceBetweenTheAxisOfThePropellerAndTheKell,
                    propellerDistanceFromQuayL: !(
                        project.data!.geometry!.typeOfQuay == ETypeOfQuay.Vertical &&
                        project.data!.engines![i].propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal
                    )
                        ? undefined
                        : x.propellerDistanceFromQuayL,
                    horizontalDistanceBetweenPropellerAxisAndTheSlopeToe: !(
                        project.data!.geometry!.typeOfQuay !== ETypeOfQuay.Vertical &&
                        project.data!.engines![i].propellerDirectionWithRespectToTheQuay == EPropellerDirectionWithRespectToTheQuay.Transversal
                    )
                        ? undefined
                        : x.horizontalDistanceBetweenPropellerAxisAndTheSlopeToe,
                };

                return (
                    <Page size='A4' style={styles.page}>
                        {header}
                        <View style={{ height: '85%' }}>
                            <View style={styles.section}>
                                <Text style={{ ...styles.h1, paddingBottom: 10 }}>{getNameScenario(x, project.data!.engines![i])}</Text>
                                <View style={{ flexDirection: 'column' }}>
                                    <View style={{ height: 398, backgroundColor: variable.greyLight }}>
                                        <Image
                                            style={{ position: 'absolute', top: 0, left: 0, height: 60 }}
                                            src={getImageFromScenario(
                                                project.data!.engines![i].typeOfPropeller,
                                                project.data!.engines![i].propellerDirectionWithRespectToTheQuay,
                                                project.data!.engines![i].numberOfPropellers,
                                            )}
                                        />
                                        <Image style={{ objectFit: 'cover' }} src={engine.data!.images[i]} />
                                    </View>
                                    <Text style={{ ...styles.h1, paddingVertical: 10 }}>{t('inputOutputScenarios')}</Text>
                                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <View style={{ width: '48%' }}>
                                            <View style={styles.tableContainer}>
                                                <View style={styles.tableHeader}>
                                                    <Text>{t('input')}</Text>
                                                </View>
                                                {Object.entries(inputScenario)
                                                    .filter(x => x[1] !== undefined)
                                                    .map((x, u) => (
                                                        <View
                                                            style={{
                                                                ...styles.tableRow,
                                                                backgroundColor: u % 2 === 0 ? variable.greyLight : variable.white,
                                                                color: u % 2 === 0 ? variable.greenLight : variable.black,
                                                            }}>
                                                            <View style={styles.tableCell}>
                                                                <Text>
                                                                    {t(x[0], {
                                                                        unitOfMeasure: (scenarioPhysicalData[x[0] as TInputScenario] ?? enginePhysicalData[x[0] as TInputEngine]).unitOfMeasure[
                                                                            project.data!.project!.unitOfMeasure!
                                                                        ],
                                                                    })}
                                                                </Text>
                                                            </View>
                                                            <View style={styles.tableCell}>
                                                                <Text>{typeof x[1] == 'boolean' ? (x[1] ? t('yes') : t('no')) : x[1] ?? '-'}</Text>
                                                            </View>
                                                        </View>
                                                    ))}
                                            </View>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '48%' }}>
                                            {Object.entries(result.data!).map(x => {
                                                if (Object.values(result.data![x[0] as TResult]![i]).some(x => x !== null && x !== false)) {
                                                    let isOk = !Object.values(result.data![x[0] as TResult]![i]).some(x => x === false);
                                                    return (
                                                        <View style={{ ...styles.tableContainer, paddingBottom: 10 }}>
                                                            <View style={styles.tableHeader}>
                                                                <Text>{t(x[0])}</Text>
                                                            </View>
                                                            {Object.entries(result.data![x[0] as TResult]![i])
                                                                .filter(y => typeof y[1] !== 'boolean')
                                                                .map((y, u) => {
                                                                    let isOk = true;
                                                                    if (y[0] === underKeelClearance) {
                                                                        isOk = result.data![x[0] as TResult]![i][isOkUnderKeelClearance];
                                                                    } else if (y[0] === minimumRevetmentThickness) {
                                                                        isOk = result.data![x[0] as TResult]![i][isOkMinimumRevetmentThickness];
                                                                    } else if (y[0] === minimumRevetmentWidthBProtection) {
                                                                        isOk = result.data![x[0] as TResult]![i][isOkMinimumRevetmentWidthBProtection];
                                                                    }
                                                                    return (
                                                                        <View
                                                                            style={{
                                                                                ...styles.tableRow,
                                                                                backgroundColor: u % 2 === 0 ? variable.greyLight : variable.white,
                                                                                color: isOk ? (u % 2 === 0 ? variable.greenLight : variable.black) : variable.red,
                                                                                border: isOk ? '' : `1px solid ${variable.red}`,
                                                                            }}>
                                                                            <View style={styles.tableCell}>
                                                                                <Text>
                                                                                    {t(y[0], {
                                                                                        unitOfMeasure:
                                                                                            resultPhysicalData[y[0] as TResultPhysicalData].unitOfMeasure[project.data!.project!.unitOfMeasure!],
                                                                                    })}
                                                                                </Text>
                                                                            </View>
                                                                            <View style={styles.tableCell}>
                                                                                <Text>{y[1] ?? '-'}</Text>
                                                                            </View>
                                                                        </View>
                                                                    );
                                                                })}
                                                        </View>
                                                    );
                                                }
                                            })}
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>
                        {footer}
                    </Page>
                );
            })}
        </Document>
    );
};

export default Pdf;
